import { useState, useEffect } from "react"
import MenuItemsTable from "../components/common/menu-items-table"
import Dropdown from "../components/common/dropdown"
import { MenuService } from "../services/MenuService"
import { RestaurantsService } from "../services/Restaurants"


export default function MenuManagement() {
  const [activeTab, setActiveTab] = useState("food")
  const [searchQuery, setSearchQuery] = useState("")
  const [selectedCategory, setSelectedCategory] = useState("All Categories")
  const [selectedLocation, setSelectedLocation] = useState("All Locations")
  const [selectedDateFilter, setSelectedDateFilter] = useState("Date Added")
  const [dishes, setDishes] = useState([])
  const [wines, setWines] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedCountry, setSelectedCountry] = useState("All Countries")
  const [selectedRegion, setSelectedRegion] = useState("All Regions")
  const [foodCategoriesOptions, setFoodCategoriesOptions] = useState([]);
  const [wineCategoriesOptions, setWineCategoriesOptions] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortBy, setSortBy] = useState(null);      // null = default
  const [sortDirection, setSortDirection] = useState('asc');
  const [foodPagination, setFoodPagination] = useState({ totalPages: 1, totalCount: 0 });
  const [winePagination, setWinePagination] = useState({ totalPages: 1, totalCount: 0 });

  console.log("🔄 Component render - activeTab:", activeTab, "sortBy:", sortBy, "sortDirection:", sortDirection, "currentPage:", currentPage);

  const handleItemsPerPageChange = (newLimit) => {
    setItemsPerPage(Number(newLimit));
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  const handleSortChange = (field) => {
    console.log("🔥 handleSortChange called with field:", field);
    console.log("Current state - sortBy:", sortBy, "sortDirection:", sortDirection);

    // Reset to first page when sorting changes
    setCurrentPage(1);

    // Determine new state
    let newSortBy = sortBy;
    let newSortDirection = sortDirection;

    if (sortBy === field) {
      // Tri-state: None → Asc → Desc → None
      if (sortDirection === 'asc') {
        newSortDirection = 'desc';
        console.log("Changed to desc for field:", field);
      } else if (sortDirection === 'desc') {
        newSortBy = null; // back to default
        newSortDirection = 'asc';
        console.log("Reset to default sort");
      }
    } else {
      // Clicking a different column: activate it and reset to ascending
      newSortBy = field;
      newSortDirection = 'asc';
      console.log("Set new sort field:", field, "direction: asc");
    }

    // Update state
    setSortBy(newSortBy);
    setSortDirection(newSortDirection);

    console.log("New state - sortBy:", newSortBy, "sortDirection:", newSortDirection);
  }

  // Year Filter State
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear.toString());

  const yearOptions = Array.from({ length: 6 }, (_, i) => {
    const y = (currentYear - i).toString();
    return { value: y, label: y };
  });

  useEffect(() => {
    RestaurantsService.getAllRestaurants('name')
      .then((data) => setRestaurants(data.restaurants || data))
      .catch(() => setRestaurants([]));
  }, []);

  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(searchQuery);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  useEffect(() => {
    fetchData(1, 5000, sortBy, sortDirection, debouncedSearchQuery, selectedCategory, selectedLocation);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortBy, sortDirection, debouncedSearchQuery, selectedCategory, selectedLocation]);

  // fetchData accepts optional pagination and sort params so the Previous Menu can request
  // server-paginated and server-sorted data when the user changes page, items per page, or column sorting.
  const fetchData = async (pageArg = 1, limitArg = 5000, sortByArg = sortBy, sortDirectionArg = sortDirection, queryArg = debouncedSearchQuery, categoryArg = selectedCategory, locationArg = selectedLocation) => {
    try {
      setLoading(true)
      const locFilter = locationArg === "All Locations" ? undefined : locationArg;
      const catFilter = categoryArg === "All Categories" ? undefined : categoryArg;

      const [dishesData, winesData] = await Promise.all([
        // Pass pagination, sort params, and search query to MenuService
        MenuService.getAllDeletedDishes(pageArg, limitArg, sortByArg, sortDirectionArg, queryArg, locFilter, catFilter),
        MenuService.getAllDeletedWines(pageArg, limitArg, sortByArg, sortDirectionArg, queryArg, locFilter, catFilter)
      ])
      setDishes(dishesData?.dishesWithRestaurant || []);
      setFoodPagination(dishesData?.pagination || { totalPages: 1, totalCount: 0 });
      setFoodCategoriesOptions(dishesData?.validDishTypes?.map((type) => ({
        value: type,
        label: type,
      })));
      setFoodCategoriesOptions((prev) => [
        ...prev,
        { value: "All Categories", label: "All Categories" },
      ]);

      setWines(winesData?.winesWithRestaurant || []);
      setWinePagination(winesData?.pagination || { totalPages: 1, totalCount: 0 });
      setWineCategoriesOptions(winesData?.wineCategories?.map((type) => ({
        value: type,
        label: type,
      })));
      setWineCategoriesOptions((prev) => [
        ...prev,
        { value: "All Categories", label: "All Categories" },
      ]);
      setError(null)
    } catch (err) {
      setError('Failed to fetch menu data')
      console.error('Error fetching menu data:', err)
    } finally {
      setLoading(false)
    }
  }



  const handleSearch = (query) => {
    setSearchQuery(query)
    setCurrentPage(1)
  }

  const handleTabChange = (tab) => {
    setSelectedCategory("All Categories");
    setActiveTab(tab)
    setCurrentPage(1)
    // Reset sorting when switching tabs
    setSortBy(null);
    setSortDirection('asc');
  }



  // Sort function for date filter
  const sortByDate = (a, b) => {
    if (selectedDateFilter === "Date Added") {
      return new Date(b.created_at) - new Date(a.created_at)
    } else if (selectedDateFilter === "Last Updated") {
      return new Date(b.updated_at) - new Date(a.updated_at)
    } else if (selectedDateFilter === "Price") {
      return (b.price || 0) - (a.price || 0)
    }
    return 0
  }

  // Update the filteredItems to use the API data
  const filteredItems = (activeTab === "food" ? dishes : wines).filter((item) => {
    // Filter by search query
    const matchesSearch = searchQuery === "" || (
      activeTab === "food"
        ? (item.name || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
        (item.type || []).join(', ').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (item.description || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
        (item.restaurantname || "").toLowerCase().includes(searchQuery.toLowerCase())
        : (item.product_name || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
        (item.category || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
        (item.producer_name || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
        (item.region?.country || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
        (item.restaurantname || "").toLowerCase().includes(searchQuery.toLowerCase())
    )

    // Filter by category
    const matchesCategory = selectedCategory === "All Categories" ||
      (activeTab === "food"
        ? item.type?.includes(selectedCategory)
        : item.category === selectedCategory)

    // Filter by location (Restaurant)
    const itemRestaurantId = item.restaurant_uuid?.uuid || item.restaurant_uuid;
    const matchesLocation = selectedLocation === "All Locations" || itemRestaurantId === selectedLocation;

    // Filter by country (wine only)
    const matchesCountry = activeTab === "food" || selectedCountry === "All Countries" ||
      item.region?.country === selectedCountry

    // Filter by region (wine only)
    const matchesRegion = activeTab === "food" || selectedRegion === "All Regions" ||
      item.region?.appellation === selectedRegion

    // Filter by year (overlap logic)
    // Item was valid if created <= yearEnd AND removed >= yearStart
    // We assume updatedAt is the removal time for "Previous Menu" items
    const yearStart = new Date(`${selectedYear}-01-01T00:00:00`);
    const yearEnd = new Date(`${selectedYear}-12-31T23:59:59`);

    const itemCreated = new Date(item.created_at || item.createdAt || 0);
    const itemRemoved = new Date(item.updated_at || item.updatedAt || Date.now());

    // Check overlap
    const matchesYear = itemCreated <= yearEnd && itemRemoved >= yearStart;

    return matchesSearch && matchesCategory && matchesLocation && matchesCountry && matchesRegion && matchesYear
  });

  // Format style data for display
  const formatStyle = (style) => {
    if (!style) return '';
    const parts = [
      style.body,
      style.sweetness,
      style.acidity,
      style.tannin,
    ].filter(Boolean);
    return parts.join(', ');
  };

  // Table sort logic — applies to sortedFilteredItems before formatting
  const sortedFilteredItems = (() => {
    // Helper to get On Menu start date for sorting
    const getOnMenuStart = (item) => {
      // Logic from calculateOnMenu - simplified for sorting
      // We need a sortable value. We can use the created_at timestamp if it falls within the year,
      // or the year start if created before.
      const yearStart = new Date(`${selectedYear}-01-01T00:00:00`).getTime();
      const yearEnd = new Date(`${selectedYear}-12-31T23:59:59`).getTime();
      const created = new Date(item.created_at || item.createdAt || 0).getTime();
      const removed = new Date(item.updated_at || item.updatedAt || Date.now()).getTime();

      // If item shouldn't be here (no overlap), return -1 or MaxInt to push to bottom
      if (created > yearEnd || removed < yearStart) return -1;

      // Start time for sorting: if created before year, effectively Jan 1 (yearStart)
      // Otherwise actual created time
      return Math.max(created, yearStart);
    };

    const getOnMenuEnd = (item) => {
      const yearStart = new Date(`${selectedYear}-01-01T00:00:00`).getTime();
      const yearEnd = new Date(`${selectedYear}-12-31T23:59:59`).getTime();
      const removed = new Date(item.updated_at || item.updatedAt || Date.now()).getTime();

      if (removed < yearStart) return -1;

      // End time for sorting: if removed after year, effectively Dec 31 (yearEnd)
      // Otherwise actual removed time
      return Math.min(removed, yearEnd);
    };

    if (activeTab === "wine") {
      // Create indexed array for stable sorting first
      const indexedItems = filteredItems.map((item, index) => ({ item, index }));

      return indexedItems.sort((a, b) => {
        const itemA = a.item;
        const itemB = b.item;

        // 1. Dynamic Comparison (Respects Sort Direction)
        const compare = (vA, vB) => {
          if (vA < vB) return sortDirection === "asc" ? -1 : 1;
          if (vA > vB) return sortDirection === "asc" ? 1 : -1;
          return 0;
        };

        // 2. Static Comparison (Always Ascending A->Z) for tie-breakers
        const compareStatic = (vA, vB) => {
          if (vA < vB) return -1;
          if (vA > vB) return 1;
          return 0;
        };

        // Helper: Format Wine Name for comparison
        const getWineName = (item) => (item.product_name || item.name || "").toLowerCase();

        // Helper: Reference to Wine Name Static Comparison (Tie-Breaker)
        const compareWineNameStatic = (itemA, itemB) => {
          return compareStatic(getWineName(itemA), getWineName(itemB));
        };

        // Helper: Final Stability (UUID or Index)
        const finalTieBreaker = () => {
          const uuidA = itemA.uuid || itemA.id || a.index;
          const uuidB = itemB.uuid || itemB.id || b.index;
          // UUID comparison for absolute stability if strings
          if (typeof uuidA === "string" && typeof uuidB === "string") {
            if (uuidA < uuidB) return -1;
            if (uuidA > uuidB) return 1;
          }
          return a.index - b.index;
        };

        // --- SORT IMPLEMENTATION ---

        // 1. Default Sort (No User Selection)
        if (!sortBy) {
          // Primary: Category A->Z
          const catA = (itemA.category || "").toLowerCase();
          const catB = (itemB.category || "").toLowerCase();
          const catRes = compareStatic(catA, catB); // Always Ascending
          if (catRes !== 0) return catRes;

          // Secondary: Wine Name A->Z
          const nameRes = compareWineNameStatic(itemA, itemB);
          if (nameRes !== 0) return nameRes;

          return finalTieBreaker();
        }

        // 2. User-Selected Sorts

        // A) Wine Name
        if (sortBy === "name") {
          const nameRes = compare(getWineName(itemA), getWineName(itemB));
          if (nameRes !== 0) return nameRes;

          // Tie-breaker: Category A->Z (Req: "Tie-breaker: Category A → Z")
          const catA = (itemA.category || "").toLowerCase();
          const catB = (itemB.category || "").toLowerCase();
          const catRes = compareStatic(catA, catB);
          if (catRes !== 0) return catRes;

          return finalTieBreaker();
        }

        // B) Vintage
        if (sortBy === "vintage") {
          const vA_raw = itemA.vintage;
          const vB_raw = itemB.vintage;
          const isNVA = !vA_raw || vA_raw === "NV" || vA_raw === "N.V.";
          const isNVB = !vB_raw || vB_raw === "NV" || vB_raw === "N.V.";

          // Numeric comparison if both are valid numbers
          if (!isNVA && !isNVB) {
            const yearA = parseInt(vA_raw) || 0;
            const yearB = parseInt(vB_raw) || 0;
            if (yearA !== yearB) return compare(yearA, yearB);
          }
          // Handle NV placement (always last)
          else if (isNVA && !isNVB) return 1;
          else if (!isNVA && isNVB) return -1;

          // Tie-breaker: Wine Name A->Z
          const nameRes = compareWineNameStatic(itemA, itemB);
          if (nameRes !== 0) return nameRes;

          return finalTieBreaker();
        }

        // C) Category
        if (sortBy === "category") {
          const catA = (itemA.category || "").toLowerCase();
          const catB = (itemB.category || "").toLowerCase();
          const res = compare(catA, catB);
          if (res !== 0) return res;

          const nameRes = compareWineNameStatic(itemA, itemB);
          if (nameRes !== 0) return nameRes;
          return finalTieBreaker();
        }

        // D) Restaurant
        if (sortBy === "restaurant") {
          const restA = (itemA.restaurantname || "").toLowerCase();
          const restB = (itemB.restaurantname || "").toLowerCase();
          const res = compare(restA, restB);
          if (res !== 0) return res;

          const nameRes = compareWineNameStatic(itemA, itemB);
          if (nameRes !== 0) return nameRes;
          return finalTieBreaker();
        }

        // E) Style
        if (sortBy === "style") {
          const styleA = formatStyle(itemA.style).toLowerCase();
          const styleB = formatStyle(itemB.style).toLowerCase();
          const res = compare(styleA, styleB);
          if (res !== 0) return res;

          const nameRes = compareWineNameStatic(itemA, itemB);
          if (nameRes !== 0) return nameRes;
          return finalTieBreaker();
        }

        // F) Region
        if (sortBy === "region") {
          const regA = itemA.region || {};
          const regB = itemB.region || {};
          const countryA = (regA.country || "").toLowerCase();
          const countryB = (regB.country || "").toLowerCase();

          // 1. Country Sort
          if (countryA !== countryB) {
            if (!countryA) return 1;
            if (!countryB) return -1;
            return compare(countryA, countryB);
          }

          // 2. Sub-region Sort (within same country)
          const subA = (regA.commune_appellation || regA.sub_region || "").toLowerCase();
          const subB = (regB.commune_appellation || regB.sub_region || "").toLowerCase();
          if (subA !== subB) {
            return compare(subA, subB);
          }

          // 3. Tie-breaker: Wine Name
          const nameRes = compareWineNameStatic(itemA, itemB);
          if (nameRes !== 0) return nameRes;
          return finalTieBreaker();
        }

        // G) Offering
        if (sortBy === "offering") {
          const offA = itemA.offering || {};
          const offB = itemB.offering || {};

          // Ascending: BTG (0) < BTB (1)
          const getScore = (off) => {
            if (off.by_the_glass) return 0;
            if (off.by_the_bottle) return 1;
            return 2;
          };
          const scoreA = getScore(offA);
          const scoreB = getScore(offB);

          if (scoreA !== scoreB) return compare(scoreA, scoreB);

          const nameRes = compareWineNameStatic(itemA, itemB);
          if (nameRes !== 0) return nameRes;
          return finalTieBreaker();
        }

        // H) On Menu
        if (sortBy === "onMenuDisplay") {
          const startA = getOnMenuStart(itemA);
          const startB = getOnMenuStart(itemB);

          if (startA !== startB) return compare(startA, startB);

          const endA = getOnMenuEnd(itemA);
          const endB = getOnMenuEnd(itemB);
          if (endA !== endB) return compare(endA, endB);

          const nameRes = compareWineNameStatic(itemA, itemB);
          if (nameRes !== 0) return nameRes;
          return finalTieBreaker();
        }

        // Fallback
        return finalTieBreaker();
      }).map(({ item }) => item);
    }

    // --- FOOD SORTING LOGIC PRESERVED BELOW ---
    if (activeTab === "food") {
      if (!sortBy) {
        return filteredItems.slice().sort((a, b) => {
          const catA = (Array.isArray(a.type) ? a.type.join(", ") : a.type || "").toLowerCase();
          const catB = (Array.isArray(b.type) ? b.type.join(", ") : b.type || "").toLowerCase();
          if (catA < catB) return -1;
          if (catA > catB) return 1;
          const nameA = (a.name || "").toLowerCase();
          const nameB = (b.name || "").toLowerCase();
          if (nameA < nameB) return -1;
          if (nameA > nameB) return 1;
          return 0;
        });
      }
      // Create indexed array for stable sorting
      const indexedItems = filteredItems.map((item, index) => ({ item, index }));

      return indexedItems.sort((a, b) => {
        const itemA = a.item;
        const itemB = b.item;

        // Main comparison function - respects sortDirection
        const compare = (vA, vB) => {
          if (vA < vB) return sortDirection === "asc" ? -1 : 1;
          if (vA > vB) return sortDirection === "asc" ? 1 : -1;
          return 0;
        };

        // Dish Name tie-breaker - ALWAYS ascending (A→Z) regardless of main sort direction
        const compareDishName = (vA, vB) => {
          if (vA < vB) return -1;
          if (vA > vB) return 1;
          return 0;
        };

        // Final stability tie-breaker: use UUID or original index
        const finalTieBreaker = () => {
          const uuidA = itemA.uuid || itemA.id || a.index;
          const uuidB = itemB.uuid || itemB.id || b.index;
          if (uuidA < uuidB) return -1;
          if (uuidA > uuidB) return 1;
          return a.index - b.index; // Absolute fallback: preserve original order
        };

        if (sortBy === "name") {
          const result = compare((itemA.name || "").toLowerCase(), (itemB.name || "").toLowerCase());
          if (result !== 0) return result;
          // tie-breaker: category (stable sort)
          const categoryResult = compare(
            (Array.isArray(itemA.type) ? itemA.type.join(", ") : itemA.type || "").toLowerCase(),
            (Array.isArray(itemB.type) ? itemB.type.join(", ") : itemB.type || "").toLowerCase()
          );
          if (categoryResult !== 0) return categoryResult;
          // Final stability tie-breaker
          return finalTieBreaker();
        }

        if (sortBy === "category") {
          const result = compare(
            (Array.isArray(itemA.type) ? itemA.type.join(", ") : itemA.type || "").toLowerCase(),
            (Array.isArray(itemB.type) ? itemB.type.join(", ") : itemB.type || "").toLowerCase()
          );
          if (result !== 0) return result;
          // Final tie-breaker: Dish Name A→Z (always ascending)
          const nameResult = compareDishName((itemA.name || "").toLowerCase(), (itemB.name || "").toLowerCase());
          if (nameResult !== 0) return nameResult;
          // Final stability tie-breaker
          return finalTieBreaker();
        }
        if (sortBy === "restaurant") {
          const result = compare(
            (itemA.restaurantname || "").toLowerCase(),
            (itemB.restaurantname || "").toLowerCase()
          );
          if (result !== 0) return result;
          // Final tie-breaker: Dish Name A→Z (always ascending)
          const nameResult = compareDishName((itemA.name || "").toLowerCase(), (itemB.name || "").toLowerCase());
          if (nameResult !== 0) return nameResult;
          // Final stability tie-breaker
          return finalTieBreaker();
        }
        if (sortBy === "onMenuDisplay") {
          const parseStart = (item) => new Date(item.created_at || item.createdAt || 0);
          const parseEnd = (item) => new Date(item.updated_at || item.updatedAt || Date.now());
          const startA = parseStart(itemA), startB = parseStart(itemB);
          if (startA - startB !== 0) return sortDirection === 'asc' ? startA - startB : startB - startA;
          const endA = parseEnd(itemA), endB = parseEnd(itemB);
          if (endA - endB !== 0) return sortDirection === 'asc' ? endA - endB : endB - endA;
          // Final tie-breaker: Dish Name A→Z (always ascending)
          const nameResult = compareDishName((itemA.name || "").toLowerCase(), (itemB.name || "").toLowerCase());
          if (nameResult !== 0) return nameResult;
          // Final stability tie-breaker
          return finalTieBreaker();
        }
        if (sortBy === "updatedAt") {
          const result = compare(
            new Date(itemA.updated_at || itemA.updatedAt || 0).getTime(),
            new Date(itemB.updated_at || itemB.updatedAt || 0).getTime()
          );
          if (result !== 0) return result;
          // Final tie-breaker: Dish Name A→Z (always ascending)
          const nameResult = compareDishName((itemA.name || "").toLowerCase(), (itemB.name || "").toLowerCase());
          if (nameResult !== 0) return nameResult;
          // Final stability tie-breaker
          return finalTieBreaker();
        }
        if (sortBy === "status") {
          const getStatus = (item) => item.status || "archived";
          const result = compare(getStatus(itemA), getStatus(itemB));
          if (result !== 0) return result;
          // Final tie-breaker: Dish Name A→Z (always ascending)
          const nameResult = compareDishName((itemA.name || "").toLowerCase(), (itemB.name || "").toLowerCase());
          if (nameResult !== 0) return nameResult;
          // Final stability tie-breaker
          return finalTieBreaker();
        }
        return finalTieBreaker();
      }).map(({ item }) => item);
    }
  })();

  // Format region data for display
  const formatRegion = (region) => {
    if (!region) return '';
    const parts = [
      region.country,
      region.appellation,
      region.state,
    ].filter(Boolean);
    return parts.join(', ');
  };

  // Format dish details for display
  const formatDishDetails = (dish) => {
    // Flatten dietary_restrictions if it's an object
    let dietaryDisplay = '';
    if (dish.dietary_restrictions) {
      if (Array.isArray(dish.dietary_restrictions)) {
        dietaryDisplay = dish.dietary_restrictions.join(', ');
      } else if (typeof dish.dietary_restrictions === 'object') {
        dietaryDisplay = [
          ...(dish.dietary_restrictions.health || []),
          ...(dish.dietary_restrictions.belief || []),
          ...(dish.dietary_restrictions.lifestyle || [])
        ].join(', ');
      }
    }

    let restaurantUUID = null;
    if (dish.restaurant_uuid && typeof dish.restaurant_uuid === 'object') {
      restaurantUUID = dish.restaurant_uuid.uuid;
    } else {
      restaurantUUID = dish.restaurant_uuid;
    }

    return {
      ...dish,
      restaurant_uuid: restaurantUUID,
      typeDisplay: dish.type?.join(', ') || '',
      dietaryDisplay,
      temperatureDisplay: dish.temperature || '',
      updatedAt: dish?.updatedAt,
      categoryDisplay: dish.category?.join(', ') || '',
      restaurantNameDisplay: dish.restaurantname || ''
    };
  };

  // Format wine details for display
  const formatWineDetails = (wine) => {
    let restaurantUUID = null;
    if (wine.restaurant_uuid && typeof wine.restaurant_uuid === 'object') {
      restaurantUUID = wine.restaurant_uuid.uuid;
    } else {
      restaurantUUID = wine.restaurant_uuid;
    }
    return {
      ...wine,
      restaurant_uuid: restaurantUUID,
      restaurantname: wine.restaurantname || '',
      styleDisplay: formatStyle(wine.style),
      producerDisplay: wine.producer_name || '',
      vintageDisplay: wine.vintage || '',
      updatedAt: wine.updatedAt || "2025-04-11T16:51:03.849Z"
    };
  };

  // Calculate On Menu display string
  const calculateOnMenu = (item, year) => {
    if (!item.created_at && !item.createdAt && !item.updated_at && !item.updatedAt) return "—";

    const yearStart = new Date(`${year}-01-01T00:00:00`);
    const yearEnd = new Date(`${year}-12-31T23:59:59`);
    const created = new Date(item.created_at || item.createdAt || 0);
    const removed = new Date(item.updated_at || item.updatedAt || Date.now());

    // If item shouldn't be here (no overlap), return "—"
    if (created > yearEnd || removed < yearStart) return "—";

    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

    // Start Month: If created before this year, it started Jan 1st of this year (effectively)
    const startMonth = created < yearStart ? "Jan" : months[created.getMonth()];

    // End Month: If removed after this year, it ended Dec 31st of this year (effectively)
    const endMonth = removed > yearEnd ? "Dec" : months[removed.getMonth()];

    return `${startMonth}–${endMonth}`;
  };

  // Format items based on type
  const formattedItems = sortedFilteredItems.map(item => {
    const formatted = activeTab === "food" ? formatDishDetails(item) : formatWineDetails(item);
    return { ...formatted, onMenuDisplay: calculateOnMenu(item, selectedYear) };
  });

  // Do not short-circuit the whole page while loading or on error.
  // Let `MenuItemsTable` render an inline loading/error state like Manage Menu.

  const userRole = localStorage.getItem("userRole");

  return (
    <div className="flex min-h-screen">
      <div className="flex-1 overflow-auto">
        <main className="p-3">
          <div className="bg-white border-b border-gray-200 p-4 rounded-lg">
            <div className="px-6 py-4 flex flex-wrap items-center gap-4">
              {/* Tab Navigation */}
              <div className="flex flex-wrap gap-4">
                <button
                  onClick={() => handleTabChange("food")}
                  className={`px-4 py-2 rounded-md ${activeTab === "food" ? "bg-primary text-white" : "bg-white text-gray-700"
                    }`}
                >
                  Food
                </button>
                <button
                  onClick={() => handleTabChange("wine")}
                  className={`px-4 py-2 rounded-md ${activeTab === "wine" ? "bg-primary text-white" : "bg-white text-gray-700"
                    }`}
                >
                  Wine
                </button>
              </div>

              {/* Menu Selector */}
              {/* <div className="relative">
                <Dropdown
                  label="Menu"
                  options={menuOptions}
                  selectedOption={currentMenu}
                  onSelect={(option) => setCurrentMenu(option.value)}
                />
              </div> */}
            </div>

            <div className="mt-5 space-y-4">
              {/* Search Bar */}
              <div className="relative max-w-md w-full">
                <input
                  type="text"
                  placeholder={`Search ${activeTab === 'food' ? 'dish' : 'wine'}, category, or restaurant...`}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                />
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>

              {/* Filters Row */}
              <div className="flex flex-wrap items-center gap-3">

                {/* Category Filter */}
                <div className="w-[200px]">
                  <Dropdown
                    label="Category"
                    options={activeTab === "food" ? foodCategoriesOptions : wineCategoriesOptions}
                    selectedOption={selectedCategory}
                    onSelect={(option) => {
                      setSelectedCategory(option.value)
                      setCurrentPage(1)
                    }}
                    className="w-full"
                  />
                </div>

                {/* Restaurant Filter */}
                <div className="w-[200px]">
                  <Dropdown
                    label="Restaurant"
                    options={[
                      { value: "All Locations", label: "All Restaurants" },
                      ...restaurants.map((r) => ({
                        value: r.uuid || r.id,
                        label: r.name,
                      })),
                    ]}
                    selectedOption={selectedLocation}
                    onSelect={(option) => {
                      setSelectedLocation(option.value)
                      setCurrentPage(1)
                    }}
                    className="w-full"
                  />
                </div>

                {/* Year Filter */}
                <div className="w-[120px]">
                  <Dropdown
                    label="Year"
                    options={yearOptions}
                    selectedOption={selectedYear}
                    onSelect={(option) => {
                      setSelectedYear(option.value);
                      setCurrentPage(1);
                      // Reset sorting when year filter changes
                      setSortBy(null);
                      setSortDirection('asc');
                    }}
                    className="w-full"
                  />
                </div>

                {/* Clear All Button */}
                <button
                  onClick={() => {
                    setSearchQuery("");
                    setSelectedCategory("All Categories");
                    setSelectedLocation("All Locations");
                    setSelectedYear(currentYear.toString());
                    setCurrentPage(1);
                    // Reset sorting on clear all
                    setSortBy(null);
                    setSortDirection('asc');
                  }}
                  className="px-4 py-2 text-sm font-medium text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md transition-colors"
                >
                  Clear All
                </button>
              </div>
            </div>
          </div>

          {/* Menu Items Table */}
          <div className="overflow-x-auto">
            <MenuItemsTable
              items={formattedItems.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)}
              onEditItem={"handleEditItem"}
              activeTab={activeTab}
              previousTable={true}
              loading={loading}
              error={error}
              currentPage={currentPage}
              totalPages={Math.ceil(formattedItems.length / itemsPerPage)}
              totalItems={formattedItems.length}
              onPageChange={(page) => setCurrentPage(page)}
              itemsPerPage={itemsPerPage}
              onItemsPerPageChange={handleItemsPerPageChange}
              sortBy={sortBy}
              sortDirection={sortDirection}
              onSortChange={handleSortChange}
            />
          </div>
        </main>
      </div >
    </div >
  )
}

